import React, { useCallback, useContext } from 'react';
import { useDropzone } from 'react-dropzone';
import { useIntl } from 'react-intl';
import { initIPFS } from '../../state/ipfs';
import { appStore } from '../../state/app';

const MIN_WIDTH_RES = 500;
const MIN_HEIGHT_RES = 500;

const DropzoneInput = ({
  classCustom,
  change,
  multipleFiles,
  amountFiles = 1,
  droppedFiles,
  setDroppedFiles,
  previewImg,
  setPreviewImg,
  acceptedFileTypes,
  setResolutionWarning,
  setOpenCropper,
  previewCustomClass,
  isEdit,
  handleBadIpfs = () => {},
}) => {
  const { update } = useContext(appStore);
  const translate = useIntl();

  const convertToIpfsReadableFormat = (arrBuffer, path) => {
    const buffer = Buffer.from(arrBuffer);
    return { path, content: buffer };
  };

  const sendToIpfs = async (data, field) => {
    update('loading', true);

    const addOptions = {
      wrapWithDirectory: true,
    };

    try {
      const ipfs = await initIPFS();
      if (data?.length) {
        for await (const result of ipfs.addAll(data, addOptions)) {
          if (!result.path) {
            change({ [field]: `/ipfs/${result.cid.string}` });
          }
        }
      } else change({ [field]: '' });
    } catch (_) {
      handleBadIpfs();
    } finally {
      update('loading', false);
    }
  };

  const handleIconDrop = (icon) => {
    const reader = new window.FileReader();
    reader.readAsDataURL(icon[0]);
    reader.onloadend = () => {
      const image = new Image();
      image.src = reader.result;
      image.onload = () => {
        if (image.height >= MIN_HEIGHT_RES && image.width >= MIN_WIDTH_RES) {
          setDroppedFiles(icon);
          setResolutionWarning('');
          setOpenCropper(true);
        } else {
          setResolutionWarning(translate.formatMessage({ id: 'dropZoneInput.photoResolution' })`${MIN_WIDTH_RES}x${MIN_HEIGHT_RES}`);
          setOpenCropper(false);
        }
      };
    };
  };

  const handleMultiFilesDrop = async (f) => {
    const blobFiles = await Promise.all(f.map((el) => new Promise((resolve) => {
      const reader = new window.FileReader();
      reader.readAsArrayBuffer(el);
      reader.onloadend = () => {
        resolve(convertToIpfsReadableFormat(reader.result, el.path));
      };
    })));

    if (blobFiles?.length) await sendToIpfs(blobFiles, 'filesCidDir');
    setDroppedFiles(f);
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    const targetFiles = [...droppedFiles, ...acceptedFiles];
    if (targetFiles.length >= 10) targetFiles.splice(10);

    if (multipleFiles) await handleMultiFilesDrop(targetFiles);
    else handleIconDrop(acceptedFiles);
  }, [droppedFiles]);

  const validFile = (file) => {
    const unique = droppedFiles.map((el) => el.path).some((el, index, arr) => arr.includes(file.path));
    if (unique && droppedFiles.length) {
      return null;
    }
  };
  const { getRootProps, getInputProps } = useDropzone({
    accept: acceptedFileTypes,
    onDrop,
    maxFiles: amountFiles,
    multiple: multipleFiles,
    validator: validFile,
  });

  const removeFile = async (file) => {
    const newFiles = [...droppedFiles];
    newFiles.splice(newFiles.indexOf(file), 1);
    setDroppedFiles(newFiles);
    if (newFiles.length) await handleMultiFilesDrop(newFiles);
    else change({ filesCidDir: '' });
  };

  const removeIcon = () => {
    setPreviewImg('');
    setDroppedFiles([]);
    change({ iconCidDir: '' });
  };

  const files = droppedFiles.map((file, index) => (
    // eslint-disable-next-line react/no-array-index-key
    <li key={index + file.path} className={previewCustomClass}>
      <div>
        {index + 1}
        .
        {' '}
        {file.path}
        {' '}
      </div>
      <i className="icon-trash-alt" onClick={() => removeFile(file, index)} />
    </li>
  ));

  const customClass = `dropzone ${classCustom}`;
  const customCreator = `wizard__creator ${multipleFiles ? 'multi' : ''}`;

  return (
    <div className={`wizard__drop-zone ${acceptedFileTypes ? 'file-types' : ''}`}>
      {multipleFiles ? (
        <div className="wizard__drop-panel">
          {droppedFiles.length > 0 && (
          <ul>{files}</ul>
          )}
          {droppedFiles.length < 10 ? (
            <div className={customCreator} {...getRootProps()}>
              <div className="wizard__creator-wrapper">
                <input {...getInputProps()} />
                <i className="icon-add-circle" />
                <span className="wizard__icon-plus-label">
                  {translate.formatMessage({ id: 'button-uploadFile' })}
                </span>
              </div>
            </div>
          ) : null}
        </div>
      ) : (
        <>
          <div {...getRootProps({ className: customClass })}>
            <input {...getInputProps()} />
            <i className="icon-add-photo" />
            {previewImg && <div className="wizard__drop-icon"><img src={previewImg} alt="alt" /></div>}
          </div>
          <div className="wizard__drop-panel">
            <div className={customCreator} {...getRootProps()}>
              {previewImg
                ? (
                  <div className="wizard__creator-wrapper">
                    <input {...getInputProps()} />
                    <i className="icon-reload-circle" />
                    <span>
                      {translate.formatMessage({ id: 'dropZoneInput.replaceImage' })}
                    </span>
                  </div>
                )
                : (
                  <div className="wizard__creator-wrapper">
                    <input {...getInputProps()} />
                    <i className="icon-add-circle" />
                    <span className="wizard__icon-plus-label">
                      {translate.formatMessage({ id: 'button-uploadImage' })}
                    </span>
                  </div>
                )}
            </div>
            {!isEdit && (
              <div className="wizard__creator-wrapper wizard__creator wizard__creator_delete" onClick={() => removeIcon()}>
                <i className="icon-trash" />
                <span>
                  {translate.formatMessage({ id: 'button-deleteImage' })}
                </span>
              </div>
            )}
          </div>
        </>
      )}
      {acceptedFileTypes ? <label className="wizard__file-list">{`.${acceptedFileTypes.replace(/[.]/g, ' ')} (files accepted)`}</label>
        : ''}
    </div>
  );
};

export default DropzoneInput;
